// Tenantra Inc. - Account & Data Deletion (public URL for App Store / Google Play Console)
import { ArrowLeft, Trash2, Clock, ShieldCheck, Mail } from 'lucide-react';
import { Logo } from '@/components/Logo';

const STEPS = [
  'Open the Tenantra app and sign in.',
  'Go to the Account tab.',
  'Tap "Delete account" and confirm with your password.',
];

export default function AccountDeletionPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Logo size="md" />
          <a href="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-stone-900">
            <ArrowLeft className="w-4 h-4" /> Home
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-teal-50 border border-teal-100 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-teal-700" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900">Delete your account & data</h1>
        </div>
        <p className="mt-4 text-stone-600 leading-relaxed">
          This page explains how tenants can request deletion of their Tenantra account and personal information,
          and what is removed versus retained.
        </p>

        <section className="mt-10">
          <h2 className="text-lg font-semibold text-stone-900">How to delete your account in the app</h2>
          <ol className="mt-4 space-y-3">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-stone-900 text-white text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                <span className="text-stone-700">{s}</span>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-stone-200 p-5">
            <Clock className="w-5 h-5 text-teal-700" />
            <h3 className="mt-3 font-semibold text-stone-900">7-day grace period</h3>
            <p className="mt-1.5 text-sm text-stone-600">Your request is scheduled for 7 days. Sign in again before then to cancel it. After that, deletion is permanent.</p>
          </div>
          <div className="rounded-xl border border-stone-200 p-5">
            <ShieldCheck className="w-5 h-5 text-teal-700" />
            <h3 className="mt-3 font-semibold text-stone-900">What's removed vs kept</h3>
            <p className="mt-1.5 text-sm text-stone-600">
              Removed: your login, name, email, phone and other personal details. Kept: your landlord's rent and
              tenancy records (anonymized), as required by law and our retention policy.
            </p>
          </div>
        </section>

        <section className="mt-10 rounded-xl bg-stone-50 border border-stone-200 p-5">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-stone-500 mt-0.5" />
            <div>
              <h3 className="font-semibold text-stone-900">No longer have the app?</h3>
              <p className="mt-1.5 text-sm text-stone-600">
                Email <a href="mailto:privacy@tenantra.ca" className="text-teal-700 font-medium underline">privacy@tenantra.ca</a> from
                the address on your account and we'll process your deletion request.
              </p>
            </div>
          </div>
        </section>

        <p className="mt-10 text-xs text-stone-400">
          See our <a href="/privacy-policy" className="underline">Privacy Policy</a> for full details on data handling and retention.
        </p>
      </main>
    </div>
  );
}
